if (!customElements.get("product-texture-picker")) {
  customElements.define(
    "product-texture-picker",
    class ProductTexturePicker extends HTMLElement {
      constructor() {
        super();
        this.options = [];
        this.variants = [];
        this.optionName = "Texture";
        this.defaultValue = "Twill Weave Glossy";
        this.surchargePercent = 30;
        this.sectionId = this.dataset.section;
        this.productUrl = this.dataset.productUrl || "";
        this.onOptionClick = this.onOptionClick.bind(this);
      }

      connectedCallback() {
        this.options = JSON.parse(this.querySelector('[data-id="texture-options"]')?.textContent || "[]");
        this.variants = JSON.parse(this.querySelector('[data-id="texture-variants"]')?.textContent || "[]");
        this.optionName = this.dataset.optionName || "Texture";
        this.defaultValue = this.dataset.defaultValue || "Twill Weave Glossy";
        this.surchargePercent = Number(this.dataset.surchargePercent || 30);
        this.productUrl = this.dataset.productUrl || "";
        this.textureCacheVersion = this.dataset.textureCacheVersion || "5";

        this.variantSelects = this.getVariantSelects();
        this.bindOptions();
        this.bindPreviewPanel();
        this.bindMoreOptionsLink();
        this.refreshTextureImages();
        this.updateAvailability();
        this.initDefaultSelection();

        requestAnimationFrame(() => {
          this.syncFromVariantSelects();
          this.updateAvailability();
        });

        this.variantSelects?.addEventListener("change", () => {
          this.syncFromVariantSelects();
          this.updateAvailability();
        });
      }

      updateAvailability() {
        this.querySelectorAll("[data-texture-value]").forEach((button) => {
          const available = this.isTextureAvailable(button.dataset.textureValue);
          button.classList.toggle("is-unavailable", !available);
          button.disabled = !available;
        });
      }

      getVariantSelects() {
        const section = document.getElementById(`shopify-section-${this.sectionId}`);
        return section?.querySelector("variant-radios, variant-selects") || null;
      }

      bindOptions() {
        this.querySelectorAll("[data-texture-value]").forEach((button) => {
          button.addEventListener("click", this.onOptionClick);
        });
      }

      bindMoreOptionsLink() {
        this.querySelectorAll("[data-scroll-to-textures]").forEach((link) => {
          link.addEventListener("click", (event) => {
            event.preventDefault();
            const target = document.getElementById("custom-carbon-fiber-options");
            if (!target) {
              return;
            }

            target.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        });
      }

      bindPreviewPanel() {
        this.previewPanel = this.querySelector(".product-texture-picker-preview-panel");
        this.previewImage = this.querySelector(".product-texture-picker-preview-panel-image");
        this.previewCaption = this.querySelector(".product-texture-picker-preview-panel-caption");
      }

      withTextureCacheBust(url) {
        if (!url) return "";

        try {
          const parsed = new URL(url, window.location.origin);
          parsed.searchParams.set("cb", this.textureCacheVersion);
          return parsed.toString();
        } catch {
          const joiner = url.includes("?") ? "&" : "?";
          return `${url}${joiner}cb=${this.textureCacheVersion}`;
        }
      }

      getTextureImageUrl(textureValue) {
        const target = this.normalizeValue(textureValue);
        const option = this.options.find(
          (entry) => this.normalizeValue(entry.value) === target,
        );

        return this.withTextureCacheBust(option?.image || "");
      }

      setImageSource(image, imageUrl) {
        if (!image || !imageUrl) return;
        image.loading = "eager";
        image.setAttribute("data-texture-src", imageUrl);
        if (image.src !== imageUrl) {
          image.src = imageUrl;
          return;
        }
        image.removeAttribute("src");
        image.src = imageUrl;
      }

      refreshTextureImages() {
        this.options = this.options.map((option) => ({
          ...option,
          image: this.withTextureCacheBust(option.image),
        }));

        this.querySelectorAll("[data-texture-value]").forEach((button) => {
          const imageUrl = this.getTextureImageUrl(button.dataset.textureValue);
          const image = button.querySelector(".product-texture-picker-option-image");
          this.setImageSource(image, imageUrl);
        });

        const selected = this.querySelector(".product-texture-picker-option.is-selected");
        if (selected) {
          this.showPreviewForButton(selected);
        } else if (this.previewImage) {
          this.setImageSource(this.previewImage, this.getTextureImageUrl(this.defaultValue));
        }
      }

      getDefaultButton() {
        return (
          this.querySelector(
            `[data-texture-value="${CSS.escape(this.defaultValue)}"]:not(.is-unavailable)`,
          ) || this.querySelector("[data-texture-value]:not(.is-unavailable)")
        );
      }

      initDefaultSelection() {
        const defaultButton = this.getDefaultButton();
        if (!defaultButton) {
          return;
        }

        const textureValue = defaultButton.dataset.textureValue;
        this.setSelectedUI(textureValue);
        this.showPreviewForButton(defaultButton);

        window.requestIdleCallback?.(
          () => {
            this.selectTexture(textureValue);
          },
          { timeout: 1500 },
        ) || window.setTimeout(() => this.selectTexture(textureValue), 0);
      }

      showPreviewForButton(button) {
        if (!this.previewPanel || !this.previewImage || button?.classList.contains("is-unavailable")) {
          return;
        }

        const label = button.querySelector(".product-texture-picker-option-label");
        const textureValue = button.dataset.textureValue || label?.textContent?.trim() || "";
        const imageUrl = this.getTextureImageUrl(textureValue);
        if (!imageUrl) {
          return;
        }

        this.setImageSource(this.previewImage, imageUrl);
        this.previewImage.alt = textureValue || label?.textContent?.trim() || "";
        if (this.previewCaption) {
          this.previewCaption.textContent = textureValue || label?.textContent?.trim() || "";
        }
        this.previewPanel.classList.add("is-active");
      }

      normalizeValue(value) {
        return (value || "").trim().toLowerCase();
      }

      getVariantOptionValues(variant) {
        if (Array.isArray(variant?.options) && variant.options.length > 0) {
          return variant.options.filter(Boolean);
        }

        return [variant?.option1, variant?.option2, variant?.option3].filter(Boolean);
      }

      findVariantByTexture(textureValue) {
        const target = this.normalizeValue(textureValue);
        const matches = this.variants.filter((variant) => {
          return this.getVariantOptionValues(variant).some(
            (option) => this.normalizeValue(option) === target,
          );
        });

        if (matches.length === 0) {
          return null;
        }

        if (matches.length === 1) {
          return matches[0];
        }

        const checkedOptions = this.getCheckedOptionValues();
        if (checkedOptions.length > 0) {
          const matchedWithSelection = matches.find((variant) => {
            const variantOptions = this.getVariantOptionValues(variant);
            return checkedOptions.every((value, index) => {
              if (!value) {
                return true;
              }
              return this.normalizeValue(variantOptions[index]) === this.normalizeValue(value);
            });
          });

          if (matchedWithSelection) {
            return matchedWithSelection;
          }
        }

        return matches[0];
      }

      getCheckedOptionValues() {
        if (!this.variantSelects) {
          return [];
        }

        return Array.from(
          this.variantSelects.querySelectorAll("fieldset input:checked, select option:checked"),
        ).map((element) => element.value);
      }

      isTextureAvailable(textureValue) {
        const variant = this.findVariantByTexture(textureValue);
        if (!variant) {
          return false;
        }
        if (variant.available === false) {
          return false;
        }
        return true;
      }

      findNativeInput(textureValue) {
        const target = this.normalizeValue(textureValue);

        if (this.variantSelects) {
          const fieldset = this.variantSelects.querySelector(
            `fieldset[data-option-name="${CSS.escape(this.optionName)}"]`,
          );
          if (fieldset) {
            for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
              if (this.normalizeValue(input.value) === target) {
                return input;
              }
            }
          }

          const select = this.variantSelects.querySelector(
            `select[data-option-position][name="options[${this.optionName}]"], select[name="options[${this.optionName}]"]`,
          );
          if (select) {
            for (const option of select.options) {
              if (this.normalizeValue(option.value) === target) {
                return option;
              }
            }
          }
        }

        for (const input of this.querySelectorAll("[data-texture-native-input]")) {
          if (this.normalizeValue(input.value) === target) {
            return input;
          }
        }

        return null;
      }

      updateVariantFormInputs(variantId) {
        const productForms = document.querySelectorAll(
          `#product-form-${this.sectionId} input[name="id"], #product-form-installment input[name="id"]`,
        );
        productForms.forEach((input) => {
          input.value = variantId;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      getFullVariant(variant) {
        return this.variants.find((entry) => Number(entry.id) === Number(variant.id)) || variant;
      }

      syncNativeInput(textureValue) {
        const nativeInput = this.findNativeInput(textureValue);
        if (!nativeInput) {
          return null;
        }

        if (nativeInput.tagName === "OPTION") {
          nativeInput.selected = true;
        } else {
          nativeInput.checked = true;
        }

        return nativeInput;
      }

      replacePriceFromHtml(html) {
        const priceSource = html.getElementById(`price-${this.sectionId}`);
        const priceDestination = document.getElementById(`price-${this.sectionId}`);

        if (!priceSource || !priceDestination) {
          return;
        }

        priceDestination.replaceWith(priceSource.cloneNode(true));
      }

      fetchProductSectionHtml(variantId) {
        const variantSelects = this.getVariantSelects();
        const targetUrl = variantSelects?.dataset.url || this.productUrl;
        let productUrl = targetUrl;

        if (variantSelects?.getProductInfoUrl) {
          productUrl = variantSelects.getProductInfoUrl(targetUrl, variantId);
        } else {
          productUrl = `${targetUrl}?section_id=${this.sectionId}&variant=${variantId}`;
        }

        return fetch(productUrl).then((response) => response.text());
      }

      applyVariant(variant, inputId) {
        const fullVariant = this.getFullVariant(variant);
        if (!fullVariant?.id) {
          return;
        }

        this.updateVariantFormInputs(fullVariant.id);

        const variantSelects = this.getVariantSelects();
        const sectionId = this.sectionId;

        const finishUpdate = (html) => {
          if (variantSelects?.handleUpdateProductInfo) {
            variantSelects.currentVariant = fullVariant;
            variantSelects.handleUpdateProductInfo(sectionId)(html);
          }

          this.replacePriceFromHtml(html);

          document.dispatchEvent(
            new CustomEvent("product:variant-change", {
              detail: { variant: fullVariant },
            }),
          );
        };

        if (!variantSelects?.renderProductInfo) {
          this.fetchProductSectionHtml(fullVariant.id)
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, "text/html");
              finishUpdate(html);
            })
            .catch(() => {});
          return;
        }

        variantSelects.currentVariant = fullVariant;
        const targetUrl = variantSelects.dataset.url || this.productUrl;
        const productUrl = variantSelects.getProductInfoUrl(targetUrl, fullVariant.id);
        variantSelects.renderProductInfo(productUrl, inputId || null, finishUpdate);
      }

      selectTexture(textureValue) {
        const variant = this.findVariantByTexture(textureValue);
        if (!variant) {
          return false;
        }

        const nativeInput = this.syncNativeInput(textureValue);
        this.applyVariant(variant, nativeInput?.id);
        return true;
      }

      setSelectedUI(textureValue) {
        this.querySelectorAll("[data-texture-value]").forEach((button) => {
          const isSelected =
            this.normalizeValue(button.dataset.textureValue) === this.normalizeValue(textureValue);
          button.classList.toggle("is-selected", isSelected);
          button.setAttribute("aria-pressed", isSelected ? "true" : "false");
        });
        this.showPreviewForButton(
          this.querySelector(
            `[data-texture-value="${CSS.escape(textureValue)}"]:not(.is-unavailable)`,
          ),
        );
        this.notifyTextureGroupChange(textureValue);
      }

      notifyTextureGroupChange(textureValue) {
        const button = this.querySelector(
          `[data-texture-value="${CSS.escape(textureValue)}"]:not(.is-unavailable)`,
        );
        const group = button?.dataset.textureGroup || "default";

        this.dispatchEvent(
          new CustomEvent("texture-group-change", {
            bubbles: true,
            detail: {
              group,
              textureValue,
            },
          }),
        );
      }

      syncFromVariantSelects() {
        if (!this.variantSelects) {
          return;
        }

        const checked = this.variantSelects.querySelector(
          `fieldset[data-option-name="${CSS.escape(this.optionName)}"] input:checked`,
        );
        if (checked?.value) {
          this.setSelectedUI(checked.value);
          return;
        }

        const select = this.variantSelects.querySelector(
          `select[name="options[${this.optionName}]"]`,
        );
        if (select?.value) {
          this.setSelectedUI(select.value);
        }
      }

      dispatchTextureChange(textureValue) {
        const imageUrl = this.getTextureImageUrl(textureValue);
        window.ProductModelTexture?.applyTexture(imageUrl, textureValue);
      }

      onOptionClick(event) {
        const button = event.currentTarget;
        if (button.classList.contains("is-unavailable")) {
          return;
        }

        const textureValue = button.dataset.textureValue;
        if (!textureValue) {
          return;
        }

        this.setSelectedUI(textureValue);
        this.dispatchTextureChange(textureValue);

        window.requestIdleCallback?.(
          () => {
            this.selectTexture(textureValue);
          },
          { timeout: 1500 },
        ) || window.setTimeout(() => this.selectTexture(textureValue), 0);
      }
    },
  );
}
